import React, { useState } from "react";
import {
  Button,
  CheckBox,
  DatePicker,
  FullScreenLoading,
  WrappedTimeSeriesChart,
} from "src/Components";
import {
  IDailyChartSearchParams,
  IEnterPairPositionTableReport,
} from "src/Interfaces/coveredCall";
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { getGainAfterCommissionCompoundYearlyGraphDaily } from "src/Requests/coveredCall";
import { packetErrorHandler } from "src/Utils/requestUtils";
import CircularProgress from "@mui/material/CircularProgress";

interface IProps {
  row: IEnterPairPositionTableReport;
  chartGetter?: (params: IDailyChartSearchParams) => Promise<Graph_Dto>;
}

const Loading = () => (
  <div className="flex justify-center items-center h-full w-full">
    <CircularProgress />
  </div>
);

const DailyChartUI = ({
  row,
  chartGetter = getGainAfterCommissionCompoundYearlyGraphDaily,
}: IProps) => {
  const [loading, setLoading] = React.useState(false);
  const [fromDate, setFromDate] = React.useState(
    getDateFromToday(90).toISOString(),
  );
  const [toDate, setToDate] = React.useState(new Date().toISOString());
  const [excludeNonTradingDays, setExcludeNonTradingDays] =
    React.useState(true);
  const [graphData, setGraphData] = useState({} as Graph_Dto);

  React.useEffect(() => {
    setLoading(true);
    const data: IDailyChartSearchParams = {
      fromDate,
      toDate,
      key: row.key,
      excludeNonTradingDays,
    };

    chartGetter(data)
      .then((resp) => setGraphData(resp))
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFromDate = (_: string, value: any) => {
    setFromDate(value);
  };

  const handleToDate = (_: string, value: any) => {
    setToDate(value);
  };

  const onSubmit = () => {
    setLoading(true);
    const data: IDailyChartSearchParams = {
      fromDate,
      toDate,
      key: row.key,
      excludeNonTradingDays,
    };

    chartGetter(data)
      .then((resp) => setGraphData(resp))
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  function getDateFromToday(dayCount: number) {
    const targetDate = new Date();
    targetDate.setDate(new Date().getDate() - dayCount);
    return targetDate;
  }

  const setDateFromBtns = (dayCount: number) => {
    const today = new Date();
    const targetDate = getDateFromToday(dayCount);
    setFromDate(targetDate.toISOString());
    setToDate(today.toISOString());
  };

  const getDateSetterBtn = (dayCount: number) => (
    <Button
      text={`${dayCount} روز`}
      onClick={() => setDateFromBtns(dayCount)}
      variant={
        fromDate?.split("T")[0] ===
        getDateFromToday(dayCount).toISOString()?.split("T")[0]
          ? "contained"
          : "outlined"
      }
    />
  );

  return (
    <div className="flex h-full w-full min-h-0 gap-3">
      {loading ? <FullScreenLoading /> : null}
      <div className="flex w-52 shrink-0 flex-col items-start gap-3">
        <DatePicker
          label="از تاریخ"
          name="fromDate"
          onChange={handleFromDate}
          value={fromDate}
        />
        <DatePicker
          label="تا تاریخ"
          name="toDate"
          onChange={handleToDate}
          value={toDate}
        />

        <div className="flex w-full flex-row items-center justify-center gap-1">
          {getDateSetterBtn(7)}
          {getDateSetterBtn(30)}
          {getDateSetterBtn(90)}
        </div>
        <CheckBox
          checked={excludeNonTradingDays}
          label="حذف روزهای تعطیل"
          name="excludeNonTradingDays"
          onChange={(_, value) => setExcludeNonTradingDays(value)}
        />
        <Button text="جستجو" onClick={onSubmit} variant="contained" />
      </div>
      <div className="h-full w-full min-h-0 min-w-0 flex-1">
        {loading ? (
          <Loading />
        ) : (
          <WrappedTimeSeriesChart
            dataset={[graphData]}
            withDate
            YValueType="price"
            decimalPlaces={2}
            className="h-full w-full"
          />
        )}
      </div>
    </div>
  );
};

export default DailyChartUI;
